import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CheckCircle2, ChevronRight, Clock3, RefreshCw, Snowflake } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  acquireHaccpNegativePhoto,
  acquireHaccpPositivePhoto,
  completeHaccpNegativeCycle,
  completeHaccpPositiveCycle,
  getHaccpNegativeContext,
  getHaccpNegativeCycle,
  getHaccpNegativeCurrentCycle,
  getHaccpNegativeCycles,
  getHaccpPositiveContext,
  getHaccpPositiveCycle,
  getHaccpPositiveCurrentCycle,
  getHaccpPositiveCycles,
  recordHaccpNegativeMeasurement,
  recordHaccpPositiveMeasurement,
  startHaccpNegativeCycle,
  startHaccpPositiveCycle,
} from './useHaccpConfigurations';
import { apiFetchBlob } from '@/lib/supabaseClient';

const FLOWS = {
  positive: {
    title: 'Abbattimento positivo',
    kindLabel: 'positivo',
    procedureLabel: 'positiva',
    route: '/haccp/abbattimento-positivo',
    getContext: getHaccpPositiveContext,
    getCycle: getHaccpPositiveCycle,
    getCurrentCycle: getHaccpPositiveCurrentCycle,
    getCycles: getHaccpPositiveCycles,
    startCycle: startHaccpPositiveCycle,
    recordMeasurement: recordHaccpPositiveMeasurement,
    completeCycle: completeHaccpPositiveCycle,
    acquirePhoto: acquireHaccpPositivePhoto,
  },
  negative: {
    title: 'Abbattimento negativo',
    kindLabel: 'negativo',
    procedureLabel: 'negativa',
    route: '/haccp/abbattimento-negativo',
    getContext: getHaccpNegativeContext,
    getCycle: getHaccpNegativeCycle,
    getCurrentCycle: getHaccpNegativeCurrentCycle,
    getCycles: getHaccpNegativeCycles,
    startCycle: startHaccpNegativeCycle,
    recordMeasurement: recordHaccpNegativeMeasurement,
    completeCycle: completeHaccpNegativeCycle,
    acquirePhoto: acquireHaccpNegativePhoto,
  },
};

function requestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  throw new Error('IDEMPOTENCY_ID_UNAVAILABLE');
}

function dateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function durationLabel(seconds) {
  const totalMinutes = Math.max(0, Math.floor(Number(seconds || 0) / 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
}

function detailLabel(error, fallback) {
  if (error?.status === 403 || error?.status === 404) return 'Non hai accesso a questo locale o ciclo.';
  if (error?.status === 409) return 'La configurazione o il ciclo è cambiato. Aggiorna i dati e riprova.';
  if (error?.status === 422) return error.message || 'Verifica i dati richiesti dalla procedura approvata.';
  return fallback;
}

export function BlastChillingCyclePage({ variant = 'positive' }) {
  const flow = FLOWS[variant] || FLOWS.positive;
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locationId = searchParams.get('location_id') || '';
  const historyMode = location.pathname.endsWith('/storico');
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState('');
  const [loading, setLoading] = useState(Boolean(locationId));
  const [refreshing, setRefreshing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyError, setHistoryError] = useState('');
  const [cycle, setCycle] = useState(null);
  const [cycleLoading, setCycleLoading] = useState(false);
  const [equipmentId, setEquipmentId] = useState('');
  const [product, setProduct] = useState('');
  const [lot, setLot] = useState('');
  const [pendingStart, setPendingStart] = useState(null);
  const [startError, setStartError] = useState('');
  const [measurementValues, setMeasurementValues] = useState({});
  const [confirmations, setConfirmations] = useState({});
  const [files, setFiles] = useState({});
  const [captures, setCaptures] = useState({});
  const [captureErrors, setCaptureErrors] = useState({});
  const [measurementRequests, setMeasurementRequests] = useState({});
  const [measurementBusy, setMeasurementBusy] = useState('');
  const [measurementErrors, setMeasurementErrors] = useState({});
  const [completionKey, setCompletionKey] = useState('');
  const [completionNeedsNotes, setCompletionNeedsNotes] = useState(false);
  const [anomaly, setAnomaly] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [completionBusy, setCompletionBusy] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [photoUrls, setPhotoUrls] = useState({});
  const locationRequestRef = useRef(0);
  const historyRequestRef = useRef(0);

  const selectedRule = useMemo(
    () => context?.equipment?.find((rule) => rule.equipment_id === equipmentId) || null,
    [context?.equipment, equipmentId],
  );
  const cycleRule = cycle?.rule || selectedRule;
  const phases = cycle?.phases || [];
  const measurements = useMemo(() => cycle?.measurements || [], [cycle?.measurements]);
  const measuredPhaseIds = useMemo(
    () => new Set(measurements.map((measurement) => measurement.phase_id)),
    [measurements],
  );
  const completedRequired = phases.every((phase) => !phase.required || measuredPhaseIds.has(phase.id));

  const loadContext = async (showRefreshing = false, request = ++locationRequestRef.current) => {
    if (!locationId) return false;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    setContextError('');
    try {
      const result = await flow.getContext(locationId);
      if (request !== locationRequestRef.current) return false;
      setContext(result);
      setEquipmentId((current) => result.available && result.equipment?.length
        ? (result.equipment.some((item) => item.equipment_id === current)
          ? current
          : result.equipment[0].equipment_id)
        : '');
      return true;
    } catch (error) {
      if (request === locationRequestRef.current) {
        setContextError(detailLabel(error, 'Non è stato possibile caricare il piano HACCP.'));
        setContext(null);
      }
      return false;
    } finally {
      if (request === locationRequestRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  const loadCycle = async (cycleId, request = locationRequestRef.current) => {
    if (!cycleId || !locationId || request !== locationRequestRef.current) return;
    setCycleLoading(true);
    try {
      const result = await flow.getCycle(cycleId, locationId);
      if (request !== locationRequestRef.current) return;
      setCycle(result);
      setEquipmentId(result.equipment_id);
    } catch (error) {
      if (request === locationRequestRef.current) {
        setCycle(null);
        setContextError(detailLabel(error, 'Non è stato possibile riaprire il ciclo.'));
      }
    } finally {
      if (request === locationRequestRef.current) setCycleLoading(false);
    }
  };

  const loadCurrentCycle = async (request = locationRequestRef.current) => {
    if (!locationId || request !== locationRequestRef.current) return;
    try {
      const current = await flow.getCurrentCycle(locationId);
      if (request !== locationRequestRef.current) return;
      if (current.cycle_id) await loadCycle(current.cycle_id, request);
      else setCycle(null);
    } catch (error) {
      if (request === locationRequestRef.current) {
        setContextError(detailLabel(error, 'Non è stato possibile verificare il ciclo aperto.'));
      }
    }
  };

  const loadHistory = async (request = ++historyRequestRef.current) => {
    setHistoryError('');
    try {
      const result = await flow.getCycles(locationId);
      if (request === historyRequestRef.current) setHistory(result.items || []);
    } catch (error) {
      if (request === historyRequestRef.current) {
        setHistoryError(detailLabel(error, 'Non è stato possibile caricare lo storico.'));
      }
    }
  };

  useEffect(() => {
    const request = ++locationRequestRef.current;
    setContext(null);
    setCycle(null);
    setHistory([]);
    setHistoryDetail(null);
    setHistoryError('');
    setContextError('');
    setEquipmentId('');
    setProduct('');
    setLot('');
    setMeasurementValues({});
    setConfirmations({});
    setFiles({});
    setCaptures({});
    setCaptureErrors({});
    setMeasurementRequests({});
    setMeasurementErrors({});
    setMeasurementBusy('');
    setCompletionKey('');
    setCompletionNeedsNotes(false);
    setAnomaly('');
    setCorrectiveAction('');
    setCompletionError('');
    setPhotoUrls({});
    if (!locationId) {
      setLoading(false);
      return () => {
        if (locationRequestRef.current === request) locationRequestRef.current += 1;
      };
    }
    loadContext(false, request).then((loaded) => {
      if (loaded) return loadCurrentCycle(request);
      return undefined;
    });
    return () => {
      if (locationRequestRef.current === request) locationRequestRef.current += 1;
    };
  // The location is the security boundary; every location change reloads server-scoped data.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, variant]);

  useEffect(() => {
    const request = ++historyRequestRef.current;
    if (historyMode && locationId) loadHistory(request);
    return () => {
      if (historyRequestRef.current === request) historyRequestRef.current += 1;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyMode, locationId, variant]);

  useEffect(() => {
    let cancelled = false;
    const source = historyDetail || cycle;
    const photoCaptures = (source?.measurements || [])
      .filter((item) => item.capture_id)
      .map((item) => item.capture_id);
    if (!photoCaptures.length) {
      setPhotoUrls({});
      return undefined;
    }
    const urls = {};
    Promise.all(photoCaptures.map(async (captureId) => {
      try {
        const blob = await apiFetchBlob(
          `/api/haccp/blast-chilling/${variant}/captures/${encodeURIComponent(captureId)}/photo`,
        );
        urls[captureId] = URL.createObjectURL(blob);
      } catch {
        // Evidence remains private; an unavailable preview does not expose the storage path.
      }
    })).then(() => {
      if (!cancelled) setPhotoUrls(urls);
      else Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    });
    return () => {
      cancelled = true;
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [cycle, historyDetail, variant]);

  const startCycle = async () => {
    if (!selectedRule || cycle || !context?.available) return;
    setStartError('');
    const pending = pendingStart || {
      location_id: locationId,
      equipment_id: selectedRule.equipment_id,
      product_name: product.trim() || null,
      lot_code: lot.trim() || null,
      idempotency_key: requestId(),
    };
    setPendingStart(pending);
    try {
      const result = await flow.startCycle(pending);
      setPendingStart(null);
      await loadCycle(result.id);
      setProduct('');
      setLot('');
    } catch (error) {
      if (error?.status && error.status < 500) setPendingStart(null);
      setStartError(detailLabel(error, 'Avvio non confermato. Puoi riprovare: verrà riutilizzata la stessa richiesta.'));
    }
  };

  const selectFile = (phaseId, file) => {
    setFiles((current) => ({
      ...current,
      [phaseId]: file ? { file, requestId: requestId() } : null,
    }));
    setCaptures((current) => ({ ...current, [phaseId]: null }));
    setCaptureErrors((current) => ({ ...current, [phaseId]: '' }));
    setConfirmations((current) => ({ ...current, [phaseId]: false }));
  };

  const uploadPhoto = async (phase) => {
    const selection = files[phase.id];
    if (!selection || !cycle) return;
    setCaptureErrors((current) => ({ ...current, [phase.id]: '' }));
    try {
      const result = await flow.acquirePhoto(
        cycle.id, locationId, phase.id, selection.file, selection.requestId,
      );
      if (result.state !== 'READY') throw new Error('PHOTO_NOT_READY');
      setCaptures((current) => ({ ...current, [phase.id]: result }));
      setMeasurementValues((current) => ({
        ...current,
        [phase.id]: result.proposed_temperature_c == null ? '' : String(result.proposed_temperature_c),
      }));
    } catch (error) {
      setCaptureErrors((current) => ({
        ...current,
        [phase.id]: detailLabel(error, 'Foto/OCR non completati. Nessuna misurazione è stata registrata; riprova con la stessa foto.'),
      }));
    }
  };

  const submitMeasurement = async (phase) => {
    const capture = captures[phase.id];
    const value = measurementValues[phase.id] ?? '';
    if (!cycle || !confirmations[phase.id] || value === '' || measurementBusy) return;
    const proposed = capture?.proposed_temperature_c;
    const corrected = proposed != null && Number(String(value).replace(',', '.')) !== Number(proposed);
    const pending = measurementRequests[phase.id] || {
      phase_id: phase.id,
      temperature_c: String(value).replace(',', '.'),
      capture_id: capture?.id || null,
      correction_reason: corrected ? (measurementErrors[`${phase.id}:reason`] || '').trim() : null,
      idempotency_key: requestId(),
      confirmed: true,
    };
    if (corrected && !pending.correction_reason) {
      setMeasurementErrors((current) => ({ ...current, [`${phase.id}:reasonRequired`]: 'Spiega la correzione del valore proposto.' }));
      return;
    }
    setMeasurementRequests((current) => ({ ...current, [phase.id]: pending }));
    setMeasurementBusy(phase.id);
    setMeasurementErrors((current) => ({ ...current, [phase.id]: '', [`${phase.id}:reasonRequired`]: '' }));
    try {
      await flow.recordMeasurement(cycle.id, locationId, pending);
      setMeasurementRequests((current) => {
        const next = { ...current };
        delete next[phase.id];
        return next;
      });
      setConfirmations((current) => ({ ...current, [phase.id]: false }));
      await loadCycle(cycle.id);
    } catch (error) {
      if (error?.status && error.status < 500) {
        setMeasurementRequests((current) => {
          const next = { ...current };
          delete next[phase.id];
          return next;
        });
      }
      setMeasurementErrors((current) => ({
        ...current,
        [phase.id]: detailLabel(error, 'Esito non ricevuto. Riprova senza cambiare i dati: la stessa richiesta idempotente verrà reinviata.'),
      }));
    } finally {
      setMeasurementBusy('');
    }
  };

  const completeCycle = async () => {
    if (!cycle || completionBusy) return;
    const key = completionKey || requestId();
    setCompletionKey(key);
    setCompletionError('');
    setCompletionBusy(true);
    try {
      const result = await flow.completeCycle(cycle.id, locationId, {
        idempotency_key: key,
        anomaly_note: completionNeedsNotes ? anomaly.trim() || null : null,
        corrective_action: completionNeedsNotes ? correctiveAction.trim() || null : null,
      });
      await loadCycle(result.id);
      setCompletionKey('');
      setCompletionNeedsNotes(false);
      await loadHistory();
    } catch (error) {
      if (error?.status === 422) {
        setCompletionNeedsNotes(true);
        setCompletionError(detailLabel(error, 'Registra l’anomalia e l’azione correttiva richieste.'));
      } else {
        setCompletionError(detailLabel(error, 'Esito non ricevuto. Riprova: verrà riutilizzata la stessa richiesta idempotente.'));
      }
    } finally {
      setCompletionBusy(false);
    }
  };

  const viewHistoryDetail = async (item) => {
    setHistoryError('');
    try {
      setHistoryDetail(await flow.getCycle(item.id, locationId));
    } catch (error) {
      setHistoryError(detailLabel(error, 'Dettaglio non disponibile.'));
    }
  };

  if (!locationId) {
    return (
      <main className="haccp-page">
        <div className="haccp-state-panel">
          <h2>Seleziona un locale</h2>
          <p>Apri l’abbattimento dalla Dashboard HACCP del locale autorizzato.</p>
          <Link className="haccp-button haccp-button-primary" to="/haccp">Torna alla Dashboard HACCP</Link>
        </div>
      </main>
    );
  }

  const detail = historyDetail || (cycle?.state === 'COMPLETED' ? cycle : null);
  const detailMeasurements = detail?.measurements || [];

  return (
    <main className="haccp-page haccp-positive-page">
      <Link className="haccp-back-link" to={`/haccp?location_id=${encodeURIComponent(locationId)}`}>
        <ChevronRight size={15} className="haccp-back-chevron" /> Dashboard HACCP
      </Link>
      <header className="haccp-page-header">
        <div>
            <div className="haccp-eyebrow"><Snowflake size={15} /> Modulo operativo · {flow.kindLabel}</div>
          <h1>{flow.title}</h1>
          <p>{context?.location_name || 'Locale autorizzato'} · ciclo configurato nel piano HACCP approvato</p>
        </div>
        <div className="haccp-header-mark"><Snowflake size={24} /></div>
      </header>

      <nav className="haccp-positive-tabs" aria-label={`Sezioni ${flow.title.toLowerCase()}`}>
        <Link className={!historyMode ? 'is-active' : ''} to={`${flow.route}?location_id=${encodeURIComponent(locationId)}`}>Ciclo</Link>
        <Link className={historyMode ? 'is-active' : ''} to={`${flow.route}/storico?location_id=${encodeURIComponent(locationId)}`}>Storico</Link>
      </nav>

      {loading || cycleLoading ? (
        <div className="haccp-state-panel" role="status"><Clock3 size={20} /><p>Caricamento del piano e del ciclo…</p></div>
      ) : contextError ? (
        <div className="haccp-temperature-error-panel" role="alert">
          <span>{contextError}</span>
          <button type="button" className="haccp-button haccp-button-secondary" onClick={async () => {
            await loadContext(true);
            await loadCurrentCycle();
          }}>
            <RefreshCw size={15} /> Riprova
          </button>
        </div>
      ) : historyMode ? (
        <section className="haccp-positive-history">
          <div className="haccp-positive-section-heading">
            <div><h2>Storico cicli</h2><p>Locale, attrezzatura, operatore, tempi, esito e anomalie.</p></div>
            <button type="button" className="haccp-icon-refresh" aria-label="Aggiorna storico" onClick={loadHistory}><RefreshCw size={17} /></button>
          </div>
          {historyError && <p className="haccp-temperature-error" role="alert">{historyError}</p>}
          {history.length === 0 ? <p className="haccp-temperature-empty-history">Non ci sono cicli da mostrare.</p> : (
            <div className="haccp-positive-history-list">
              {history.map((item) => (
                <button type="button" key={item.id} className="haccp-positive-history-row" onClick={() => viewHistoryDetail(item)}>
                  <span>
                    <strong>{item.equipment_label || `Abbattitore ${item.equipment_number}`}</strong>
                    <small>
                      {dateTime(item.started_at)}
                      {variant === 'negative' && ` · ${item.location_name || context?.location_name}`}
                      {' · '}{item.operator_name}
                    </small>
                  </span>
                  <b className={item.result === 'NON_COMPLIANT' ? 'is-alert' : ''}>{item.result === 'COMPLIANT' ? 'Conforme' : item.result === 'NON_COMPLIANT' ? 'Da verificare' : 'In corso'}</b>
                </button>
              ))}
            </div>
          )}
          {detail && (
            <section className="haccp-positive-history-detail" aria-label="Dettaglio ciclo">
              <h3>{detail.equipment_label || flow.title}</h3>
              <p>
                {variant === 'negative' && `${detail.location_name || context?.location_name} · `}
                Avvio {dateTime(detail.started_at)} · fine {dateTime(detail.finished_at)}
                {variant === 'negative' && ` · durata ${durationLabel(detail.duration_seconds)}`}
                {' · '}versione piano {detail.configuration_version}
              </p>
              <p>Operatore: {detail.operator_name} · prodotto: {detail.product_name || '—'} · lotto: {detail.lot_code || '—'}</p>
              <p>Esito: <strong>{detail.result || 'In corso'}</strong></p>
              {detail.anomaly_note && <p>Anomalia: {detail.anomaly_note}</p>}
              {detail.corrective_action && <p>Azione correttiva: {detail.corrective_action}</p>}
              {detailMeasurements.map((measurement) => (
                <div className="haccp-positive-history-measurement" key={measurement.id}>
                  <strong>{measurement.phase_name}: {measurement.temperature_c} °C</strong>
                  <small>{dateTime(measurement.measured_at)} · {measurement.confirmation_kind === 'CONFIRMED_OCR' ? 'OCR confermato' : measurement.confirmation_kind === 'CORRECTED_OCR' ? 'OCR corretto e motivato' : 'Inserimento manuale'}</small>
                  {measurement.correction_reason && <small>Motivo correzione: {measurement.correction_reason}</small>}
                  {measurement.capture_id && photoUrls[measurement.capture_id] && (
                    <img className="haccp-temperature-photo" src={photoUrls[measurement.capture_id]} alt={`Evidenza fotografica ${measurement.phase_name}`} />
                  )}
                </div>
              ))}
            </section>
          )}
        </section>
      ) : !context?.available ? (
        <div className="haccp-state-panel">
          <div className="haccp-state-icon"><Clock3 size={20} /></div>
          <div><h2>Modulo non ancora attivo</h2><p>È disponibile quando il locale ha una procedura {flow.procedureLabel} completa e approvata.</p></div>
        </div>
      ) : cycle?.state === 'COMPLETED' ? (
        <section className="haccp-positive-success">
          <CheckCircle2 size={24} />
          <div><h2>Ciclo registrato</h2><p>Esito: {cycle.result === 'COMPLIANT' ? 'conforme alla procedura approvata' : 'non conforme, con anomalia tracciata'}.</p></div>
          <button type="button" className="haccp-button haccp-button-primary" onClick={() => {
            setCycle(null); setMeasurementValues({}); setCaptures({}); setFiles({});
            setConfirmations({}); setCompletionNeedsNotes(false); setAnomaly(''); setCorrectiveAction('');
          }}>Avvia un nuovo ciclo</button>
        </section>
      ) : !cycle ? (
        <section className="haccp-positive-start">
          <div className="haccp-positive-section-heading">
            <div><h2>Avvia un ciclo</h2><p>Orario, operatore e valutazione sono registrati dal server.</p></div>
          </div>
          <label className="haccp-field"><span>Abbattitore</span>
            <select value={equipmentId} disabled={Boolean(pendingStart)} onChange={(event) => setEquipmentId(event.target.value)}>
              {context.equipment.map((rule) => <option key={rule.equipment_id} value={rule.equipment_id}>{rule.equipment_label || `Abbattitore ${rule.equipment_number}`}</option>)}
            </select>
          </label>
          {selectedRule && <label className="haccp-field"><span>Prodotto{selectedRule.product_required ? '' : ' · facoltativo'}</span><input maxLength="160" required={selectedRule.product_required} value={product} disabled={Boolean(pendingStart)} onChange={(event) => setProduct(event.target.value)} /></label>}
          {selectedRule && <label className="haccp-field"><span>Lotto{selectedRule.lot_required ? '' : ' · facoltativo'}</span><input maxLength="120" required={selectedRule.lot_required} value={lot} disabled={Boolean(pendingStart)} onChange={(event) => setLot(event.target.value)} /></label>}
          <div className="haccp-positive-procedure">
            <strong>Fasi previste</strong>
            {(selectedRule?.phases || []).map((phase) => <span key={phase.id}>{phase.sequence_number}. {phase.name}{phase.required ? ' · obbligatoria' : ' · facoltativa'}</span>)}
            <small>I limiti sono quelli definiti nella procedura approvata del locale.</small>
          </div>
          {startError && <p role="alert" className="haccp-temperature-error">{startError}</p>}
          <button type="button" className="haccp-button haccp-button-primary" disabled={!selectedRule || Boolean(pendingStart) || !selectedRule.phases.length} onClick={startCycle}>
            {pendingStart ? 'Riprova avvio' : 'Avvia ciclo'}
          </button>
        </section>
      ) : (
        <section className="haccp-positive-cycle">
          <div className="haccp-positive-cycle-summary">
            <div><span>In corso · {cycle.equipment_label || `Abbattitore ${cycle.equipment_number}`}</span><strong>{cycle.product_name || 'Prodotto non richiesto'}</strong></div>
            <small>Avviato {dateTime(cycle.started_at)} · Operatore {cycle.operator_name}</small>
            {cycle.product_name && <small>Lotto: {cycle.lot_code || 'non richiesto'}</small>}
            <small>Durata massima configurata: {cycleRule?.max_cycle_minutes} minuti</small>
          </div>
          <div className="haccp-positive-phase-list">
            {phases.map((phase) => {
              const saved = measurements.find((measurement) => measurement.phase_id === phase.id);
              const capture = captures[phase.id];
              const proposed = capture?.proposed_temperature_c;
              const value = measurementValues[phase.id] ?? '';
              const corrected = proposed != null && Number(String(value).replace(',', '.')) !== Number(proposed);
              const phaseFile = files[phase.id];
              const needsPhoto = cycleRule?.photo_required || phase.photo_required;
              return (
                <article className="haccp-positive-phase-card" key={phase.id}>
                  <div className="haccp-positive-phase-heading">
                    <h3>{phase.sequence_number}. {phase.name}</h3>
                    <span className={saved ? 'is-complete' : ''}>{saved ? 'Registrata' : phase.required ? 'Richiesta' : 'Facoltativa'}</span>
                  </div>
                  <p>Intervallo configurato: {phase.min_temperature_c}–{phase.max_temperature_c} °C{phase.tolerance_c ? ` · tolleranza ${phase.tolerance_c} °C` : ''}</p>
                  {phase.deadline_minutes_after_start && <small>Tempo massimo dalla partenza: {phase.deadline_minutes_after_start} minuti</small>}
                  {saved ? (
                    <div className="haccp-positive-saved-measurement"><strong>{saved.temperature_c} °C</strong><span>{dateTime(saved.measured_at)} · {saved.confirmation_kind === 'CONFIRMED_OCR' ? 'OCR confermato' : saved.confirmation_kind === 'CORRECTED_OCR' ? 'OCR corretto' : 'inserimento manuale'}</span>
                      {saved.capture_id && photoUrls[saved.capture_id] && <img className="haccp-temperature-photo" src={photoUrls[saved.capture_id]} alt={`Evidenza ${phase.name}`} />}
                    </div>
                  ) : (
                    <div className="haccp-positive-measurement-form">
                      {(cycleRule?.photo_allowed && phase.photo_allowed) && (
                        <>
                          <label className="haccp-field"><span>Foto originale {needsPhoto ? '· obbligatoria' : '· facoltativa'}</span>
                            <input type="file" accept="image/jpeg,image/png" capture="environment" disabled={Boolean(measurementRequests[phase.id])}
                              onChange={(event) => selectFile(phase.id, event.target.files?.[0] || null)} />
                          </label>
                          {phaseFile?.file && !capture && <button type="button" className="haccp-button haccp-button-secondary"
                            disabled={!phaseFile || !!measurementRequests[phase.id]}
                            onClick={() => uploadPhoto(phase)}><Camera size={15} /> {captureErrors[phase.id] ? 'Riprova foto/OCR' : 'Carica foto e leggi proposta'}</button>}
                          {captureErrors[phase.id] && <p role="alert" className="haccp-temperature-error">{captureErrors[phase.id]}</p>}
                          {capture && <div className="haccp-temperature-ocr" role="status">
                            <strong>Proposta OCR: {proposed == null ? 'non disponibile' : `${proposed} °C`}</strong>
                            <span>La foto non registra automaticamente la temperatura. Controlla il dato e confermalo o correggilo.</span>
                          </div>}
                        </>
                      )}
                      <label className="haccp-field"><span>Temperatura verificata °C</span>
                        <input aria-label={`Temperatura verificata ${phase.name}`} inputMode="decimal" pattern="[+-]?[0-9]+([.,][0-9]{1,2})?"
                          value={value} disabled={Boolean(measurementRequests[phase.id])}
                          onChange={(event) => { setMeasurementValues((current) => ({ ...current, [phase.id]: event.target.value })); setConfirmations((current) => ({ ...current, [phase.id]: false })); }} />
                      </label>
                      {corrected && <label className="haccp-field"><span>Motivo correzione OCR</span>
                        <textarea maxLength="500" value={measurementErrors[`${phase.id}:reason`] || ''} disabled={Boolean(measurementRequests[phase.id])}
                          onChange={(event) => setMeasurementErrors((current) => ({ ...current, [`${phase.id}:reason`]: event.target.value }))} />
                      </label>}
                      {measurementErrors[`${phase.id}:reasonRequired`] && <p role="alert" className="haccp-temperature-error">{measurementErrors[`${phase.id}:reasonRequired`]}</p>}
                      {!needsPhoto && !capture && <p className="haccp-temperature-instructions">Registrazione manuale secondo la procedura approvata. La foto non è richiesta.</p>}
                      {needsPhoto && !capture && <p className="haccp-temperature-instructions">Carica e verifica una foto prima di registrare questa misurazione.</p>}
                      <label className="haccp-temperature-checkline"><input type="checkbox" checked={Boolean(confirmations[phase.id])} disabled={!value || (needsPhoto && !capture) || Boolean(measurementRequests[phase.id])}
                        onChange={(event) => setConfirmations((current) => ({ ...current, [phase.id]: event.target.checked }))} />Ho verificato e confermo questa misurazione.</label>
                      {measurementErrors[phase.id] && <p role="alert" className="haccp-temperature-error">{measurementErrors[phase.id]}</p>}
                      <button type="button" className="haccp-button haccp-button-secondary"
                        disabled={!value || !confirmations[phase.id] || (needsPhoto && !capture) || measurementBusy === phase.id}
                        onClick={() => submitMeasurement(phase)}>
                        {measurementBusy === phase.id ? 'Registrazione…' : measurementRequests[phase.id] ? 'Riprova registrazione' : 'Conferma fase'}
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          <section className="haccp-positive-completion">
            <p>{completedRequired ? 'Tutte le fasi obbligatorie sono registrate.' : 'Mancano fasi obbligatorie. Se il ciclo viene interrotto, il sistema lo registrerà come non conforme e richiederà una nota.'}</p>
            {completionNeedsNotes && <>
              <p className="haccp-temperature-error">{cycleRule?.nonconformity_procedure}</p>
              <label className="haccp-field"><span>Descrivi l’anomalia</span>
                <textarea maxLength="2000" required value={anomaly} disabled={completionBusy} onChange={(event) => setAnomaly(event.target.value)} />
              </label>
              {cycleRule?.corrective_action_required && <label className="haccp-field"><span>Azione correttiva eseguita</span>
                <textarea maxLength="2000" required value={correctiveAction} disabled={completionBusy} onChange={(event) => setCorrectiveAction(event.target.value)} />
              </label>}
            </>}
            {completionError && <p role="alert" className="haccp-temperature-error">{completionError}</p>}
            <button type="button" className="haccp-button haccp-button-primary" disabled={completionBusy || (completionNeedsNotes && (!anomaly.trim() || (cycleRule?.corrective_action_required && !correctiveAction.trim())))}
              onClick={completeCycle}>{completionBusy ? 'Conclusione…' : 'Concludi ciclo'}</button>
          </section>
        </section>
      )}
    </main>
  );
}

export default function PositiveBlastChillingPage() {
  return <BlastChillingCyclePage variant="positive" />;
}
